import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowDownRight, Terminal } from "lucide-react";
import { RevealLines, Overline } from "../Reveal";

const HERO_IMG =
  "https://images.unsplash.com/photo-1627819098699-d8ae75db1112?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600";

export const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start start", "end start"],
  });
  const yImg = useTransform(scrollYProgress, [0, 1], ["0%", "35%"]);
  const scaleImg = useTransform(scrollYProgress, [0, 1], [1, 1.15]);
  const yText = useTransform(scrollYProgress, [0, 1], ["0%", "-20%"]);
  const opacity = useTransform(scrollYProgress, [0, 0.7], [1, 0]);

  return (
    <section
      id="top"
      ref={ref}
      className="relative min-h-[100svh] w-full overflow-hidden"
      data-testid="hero"
    >
      {/* Parallax image + glows */}
      <motion.div style={{ y: yImg, scale: scaleImg }} className="absolute inset-0">
        <img
          src={HERO_IMG}
          alt=""
          className="h-full w-full object-cover opacity-40"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-[#05070f]/50 via-[#05070f]/70 to-[#05070f]" />
      </motion.div>

      <div className="absolute inset-0 grid-lines opacity-40" />
      <div className="animate-floaty absolute -left-40 top-24 h-[26rem] w-[26rem] rounded-full bg-cyber-violet/25 blur-[120px]" />
      <div className="animate-floaty absolute -right-32 bottom-10 h-[28rem] w-[28rem] rounded-full bg-cyber-cyan/20 blur-[130px]" style={{ animationDelay: "-6s" }} />

      <motion.div
        style={{ y: yText, opacity }}
        className="relative z-10 mx-auto flex min-h-[100svh] max-w-7xl flex-col justify-center px-5 pb-20 pt-28 md:px-10"
      >
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.2 }}
        >
          <Overline>Elite Cyber Defense · Beyond Cybersecurity</Overline>
        </motion.div>

        <h1 className="mt-6 font-head text-[10vw] font-black leading-[0.95] tracking-tighter text-white sm:text-8xl sm:leading-[0.85] lg:text-[8.5rem]">
          <RevealLines lines={["DEFENDING", "TOMORROW."]} delay={0.35} />
        </h1>

        <div className="mt-8 flex flex-col gap-8 md:flex-row md:items-end md:justify-between">
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1, duration: 0.8 }}
            className="max-w-md font-sans text-base leading-relaxed text-white/70 md:text-lg"
          >
            Cyber Defense de élite para el hacker ético del futuro. Combino{" "}
            <span className="text-cyber-cyan">IA, OSINT, pentesting</span> y
            ciberinteligencia para blindar tu empresa antes de que exista la amenaza.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1.15, duration: 0.8 }}
            className="flex flex-wrap items-center gap-4"
          >
            <a
              href="#contacto"
              data-testid="hero-cta"
              className="group inline-flex items-center gap-2 rounded-full bg-cyber-cyan px-7 py-3.5 font-mono-tech text-xs font-bold uppercase tracking-widest text-[#04060a] transition-transform duration-300 hover:scale-105"
            >
              Auditar mi empresa
              <ArrowDownRight className="h-4 w-4 transition-transform duration-300 group-hover:rotate-45" />
            </a>
            <a
              href="#servicios"
              data-testid="hero-secondary"
              className="inline-flex items-center gap-2 rounded-full border border-white/15 px-7 py-3.5 font-mono-tech text-xs uppercase tracking-widest text-white/80 transition-colors duration-300 hover:border-cyber-cyan hover:text-cyber-cyan"
            >
              <Terminal className="h-4 w-4" /> Ver capacidades
            </a>
          </motion.div>
        </div>
      </motion.div>
    </section>
  );
};
